import * as THREE from 'three';

/**
 * Procedural cloud-to-ground lightning: a fractal (midpoint-displaced) channel with side branches,
 * rendered as camera-facing ribbons in HDR straight into the composite buffer for a few frames.
 */
export class Lightning {
  readonly scene = new THREE.Scene();
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private life = 0;

  constructor() {
    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: { uIntensity: { value: 0 } },
      vertexShader: /* glsl */ `
        in vec3 aDir;
        in float aSide;
        in float aWidth;
        out float vSide;
        void main() {
          vec3 toCam = normalize(cameraPosition - position);
          vec3 side = normalize(cross(aDir, toCam));
          vec3 p = position + side * aSide * aWidth;
          vSide = aSide;
          gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uIntensity;
        in float vSide;
        out vec4 fragColor;
        void main() {
          float core = exp(-vSide * vSide * 6.0);
          vec3 c = vec3(0.75, 0.82, 1.0) * core * uIntensity * 140.0;
          fragColor = vec4(c, 1.0);
        }`,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.scene.add(this.mesh);
  }

  strike(top: THREE.Vector3, groundY: number) {
    const segs: [THREE.Vector3, THREE.Vector3, number][] = [];
    const bolt = (a: THREE.Vector3, b: THREE.Vector3, w: number, depth: number) => {
      const pts = [a, b];
      let disp = a.distanceTo(b) * 0.18;
      for (let it = 0; it < 6; it++) {
        const out: THREE.Vector3[] = [pts[0]];
        for (let i = 0; i < pts.length - 1; i++) {
          const m = pts[i].clone().lerp(pts[i + 1], 0.5);
          m.x += (Math.random() - 0.5) * disp;
          m.z += (Math.random() - 0.5) * disp;
          m.y += (Math.random() - 0.5) * disp * 0.3;
          out.push(m, pts[i + 1]);
        }
        pts.splice(0, pts.length, ...out);
        disp *= 0.55;
      }
      for (let i = 0; i < pts.length - 1; i++) {
        segs.push([pts[i], pts[i + 1], w]);
        if (depth < 2 && Math.random() < 0.06) {
          const dir = pts[i + 1].clone().sub(pts[i]).normalize();
          const len = (pts[i].y - groundY) * (0.15 + Math.random() * 0.3);
          const end = pts[i].clone().add(new THREE.Vector3(dir.x + (Math.random() - 0.5) * 1.5, -0.7, dir.z + (Math.random() - 0.5) * 1.5).normalize().multiplyScalar(len));
          bolt(pts[i], end, w * 0.45, depth + 1);
        }
      }
    };
    const ground = new THREE.Vector3(top.x + (Math.random() - 0.5) * 600, groundY, top.z + (Math.random() - 0.5) * 600);
    bolt(top, ground, 9, 0);
    const pos: number[] = [], dir: number[] = [], side: number[] = [], width: number[] = [], idx: number[] = [];
    for (const [a, b, w] of segs) {
      const d = b.clone().sub(a).normalize();
      const base = pos.length / 3;
      for (const [p, s] of [[a, -1], [a, 1], [b, -1], [b, 1]] as [THREE.Vector3, number][]) {
        pos.push(p.x, p.y, p.z); dir.push(d.x, d.y, d.z); side.push(s); width.push(w);
      }
      idx.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aDir', new THREE.Float32BufferAttribute(dir, 3));
    g.setAttribute('aSide', new THREE.Float32BufferAttribute(side, 1));
    g.setAttribute('aWidth', new THREE.Float32BufferAttribute(width, 1));
    g.setIndex(idx);
    this.mesh.geometry.dispose();
    this.mesh.geometry = g;
    this.life = 0.55;
    this.mesh.visible = true;
  }

  update(dt: number, flash: number) {
    if (this.life <= 0) { this.mesh.visible = false; return; }
    this.life -= dt;
    this.material.uniforms.uIntensity.value = Math.min(1, flash * 1.4) * (this.life > 0 ? 1 : 0);
  }

  get active() {
    return this.mesh.visible;
  }
}
